import type { CSSProperties } from '@mui/styles';
import classNames from 'classnames';
import * as React from 'react';
import Tooltip from './Tooltip';
export type Props = {
  /**
   * checked status (state maintained by parent)
   */
  checked: boolean;

  /**
   * used for input id & data-testid. NOTE: must be unique for each checkbox
   * on the page or they will not work independently.
   */
  id: string;

  /**
   * custom className for outer div
   */
  className?: string;

  /**
   * disable checked status change
   */
  disabled?: boolean;

  /**
   * additional inline styles to apply for checkbox container
   */
  style?: CSSProperties;

  /**
   * tooltip to show on hover of a disabled checkbox. Will only appear when
   * 'disabled' field is true.
   */
  disabledTooltip?: string | React.ReactElement<any>;

  /**
   * whether clicking a label checks the box.
   * if this prop is set to true, selecting a label will have no affect on the state of the checkbox
   */
  disableLabelClick?: boolean;

  /**
   * label is what will appear to the right of the checkbox, and can be
   * a string or an element.
   */
  label?: string | React.ReactElement<any>;

  /**
   * custom className for label
   */
  labelClassName?: string;

  /**
   * access checked value in callback with e.target.checked
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;

  /**
   * if tooltip and label passed in, an info icon will appear inline next
   * to the label.
   */
  tooltip?: string | React.ReactElement<any>;
};
// SUBCOMPONENT OF CHECKBOX:
type labelProps = {
  id: string;
  className?: string;
  disabled?: boolean;
  disableLabelClick?: boolean;
  label?: string | React.ReactElement<any>;
  tooltip?: string | React.ReactElement<any>;
};

const Label = (props: labelProps): React.ReactElement<any> => {
  let checkboxLabel: JSX.Element | null = null;
  const textClassName = classNames('check_box--label-text', {
    'check_box--label-text-disable-click': props.disableLabelClick,
  });

  if (props.label) {
    checkboxLabel = <span className={classNames(textClassName, props.className)}>{props.label}</span>;
  }

  if (props.label && props.tooltip) {
    checkboxLabel = (
      <span className={textClassName}>
        <span className="check_box--popover-label">
          {props.label}
          <Tooltip body={props.tooltip} infoIconClassName="h_va-u" />
        </span>
      </span>
    );
  }

  return (
    <label
      className={classNames('check_box--label', {
        disabled: props.disabled,
      })}
      htmlFor={`${props.id}-checkbox`}
    >
      {checkboxLabel}
    </label>
  );
};

class Checkbox extends React.PureComponent<Props> {
  static defaultProps = {
    checked: false,
    disabled: false,
    onChange: () => {},
  };

  render() {
    let checkbox = (
      <div>
        <input
          className="check_box--input"
          checked={this.props.checked}
          disabled={this.props.disabled}
          id={`${this.props.id}-checkbox`}
          name="check"
          onChange={this.props.onChange}
          type="checkbox"
        />
        <Label
          className={this.props.labelClassName}
          label={this.props.label || ''}
          tooltip={this.props.tooltip}
          disabled={this.props.disabled}
          id={this.props.id}
          disableLabelClick={this.props.disableLabelClick}
        />
      </div>
    );

    if (this.props.disabled && this.props.disabledTooltip) {
      checkbox = <Tooltip body={this.props.disabledTooltip}>{checkbox}</Tooltip>;
    }

    return (
      <div
        data-testid={`${this.props.id}-checkbox`}
        className={classNames('check_box', this.props.className)}
        bindClickEvent={true}
        // @ts-ignore
        style={this.props.style}
      >
        {checkbox}
      </div>
    );
  }
}

export default Checkbox;
